import { Box, Chip, Stack, Typography } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";

import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { renderIf } from "@/common/helpers/render/renderIf";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import Datetime from "../../Datetime/Datetime";
import FileListView from "../../Files/FileListView";
import { PdfViewerModal } from "../../Files/PDF/PdfViewerModal";
import FieldValue from "../../Form/Display/FieldValue";
import AppIcon from "../../Icons/AppIcon";
import { CustomerAcknowledgementDisplayModal } from "../Customer/Acknowledgement/CustomerAcknowledgementDisplayModal";
import DepartmentLink from "../Department/DepartmentLink";
import LocationLink from "../Location/LocationLink";
import ProductLocationLink from "../ProductLocation/ProductLocationLink";
import SpotLink from "../Spot/SpotLink";
import VisualInspectionLink from "../VisualInspection/VisualInspectionLink";

interface Props {
  // title: ReactNode;
  contract: ContractDto;
  spotInfoType: "CheckOut" | "CheckIn";
  // info: ContractSpotInfoDto | null | undefined;
  action?: ReactNode;
  onUpdate: (newValue: ContractDto) => void;
}

export default function ContractSpotInfoDisplay({
  contract,
  spotInfoType,
  action,
  onUpdate,
}: Props) {
  const [fileItemPdfToViewInModalOpen, setFileItemPdfToViewInModalOpen] = useState<FileItem | null>(
    null,
  );
  const [isVehicleStateAckViewModalOpen, setIsVehicleStateAckViewModalOpen] = useState(false);

  const info = useMemo(
    () =>
      spotInfoType === "CheckOut"
        ? contract.checkOutSpotInfo
        : spotInfoType === "CheckIn"
          ? contract.checkInSpotInfo
          : undefined,
    [contract, spotInfoType],
  );
  const vehicleStatePdfFileItem = useMemo(
    () =>
      info?.vehicleActualStatePdf ? FileItem.createFrom(info.vehicleActualStatePdf) : undefined,
    [info?.vehicleActualStatePdf],
  );
  const vehicleStateAckPdfFileItem = useMemo(
    () =>
      info?.vehicleStateAck?.vehicleActualStatePdf
        ? FileItem.createFrom(info.vehicleStateAck.vehicleActualStatePdf)
        : undefined,
    [info?.vehicleStateAck?.vehicleActualStatePdf],
  );

  const checkLabel =
    spotInfoType === "CheckOut" ? "Check out" : spotInfoType === "CheckIn" ? "Check in" : undefined;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0fr", gap: 2 }}>
        <Typography variant='h3'>{checkLabel}</Typography>

        <Box>{action}</Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xxs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(4, 1fr)",
          },
          gridTemplateRows: "auto",
          rowGap: 1,
          columnGap: 1,
        }}
      >
        <FieldValue variant='filled' direction='column' label='Status'>
          {info?.isCompleted && (
            <>
              <Chip size='small' variant='filled' color='success' label='Completed' />{" "}
            </>
          )}

          {renderIf()
            .if(spotInfoType === "CheckOut")
            .then(
              <>
                {contract?.canCheckOut && (
                  <>
                    <Chip
                      size='small'
                      variant='outlined'
                      color='success'
                      label='Can be checked out'
                    />{" "}
                  </>
                )}
                {contract?.isPreparedForCheckOut && (
                  <>
                    <Chip
                      size='small'
                      variant='outlined'
                      color='success'
                      label='Prepared for check out'
                    />{" "}
                  </>
                )}
              </>,
            )
            .elseif(spotInfoType === "CheckIn")
            .then(
              <>
                {contract?.canCheckIn && (
                  <>
                    <Chip
                      size='small'
                      variant='outlined'
                      color='success'
                      label='Can be checked in'
                    />{" "}
                  </>
                )}
                {contract?.isPreparedForCheckIn && (
                  <>
                    <Chip
                      size='small'
                      variant='outlined'
                      color='success'
                      label='Prepared for check in'
                    />{" "}
                  </>
                )}
              </>,
            )
            .else("-")
            .render()}
        </FieldValue>

        <FieldValue variant='filled' direction='column' label='Date' isEmpty={!info?.date}>
          <Datetime datetime={info?.date} />
        </FieldValue>

        {info?.departmentId && (
          <FieldValue
            variant='filled'
            direction='column'
            label='Department'
            isEmpty={!info?.departmentId}
          >
            <DepartmentLink entity={undefined} entityId={info?.departmentId} />
          </FieldValue>
        )}

        {info?.locationId && (
          <FieldValue
            variant='filled'
            direction='column'
            label='Location'
            isEmpty={!info?.locationId}
          >
            <LocationLink entity={undefined} entityId={info?.locationId} />
          </FieldValue>
        )}

        {info?.productLocationId && (
          <FieldValue
            variant='filled'
            direction='column'
            label='Sales location'
            isEmpty={!info?.productLocationId}
          >
            <ProductLocationLink entity={undefined} entityId={info?.productLocationId} />
          </FieldValue>
        )}

        <FieldValue variant='filled' direction='column' label='Spot' isEmpty={!info?.spot?.id}>
          <SpotLink entity={undefined} entityId={info?.spot?.id} />
        </FieldValue>

        <FieldValue
          variant='filled'
          direction='column'
          label='Visual inspection'
          isEmpty={!info?.visualInspectionId}
        >
          <VisualInspectionLink
            entity={undefined}
            entityId={info?.visualInspectionId || undefined}
          />
        </FieldValue>

        <FieldValue variant='filled' direction='column' label='Notes' isEmpty={!info?.notes}>
          {info?.notes}
        </FieldValue>

        {info?.vehicleActualStatePdf && (
          <FieldValue
            variant='filled'
            direction='column'
            label='Vehicle state PDF'
            isEmpty={!info?.vehicleActualStatePdf}
          >
            {vehicleStatePdfFileItem && (
              <FileListView
                files={FileItem.createManyFrom([vehicleStatePdfFileItem])}
                maxVisibleFiles={1}
                itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                fileItemViewProps={{
                  actions: {
                    enabled: true,
                    click: true,
                    quickView: true,
                    download: true,
                    openInNewTab: true,
                    onClick: (fileItem) => {
                      setFileItemPdfToViewInModalOpen(fileItem);
                    },
                    onQuickView: (fileItem) => {
                      setFileItemPdfToViewInModalOpen(fileItem);
                    },
                  },
                }}
              />
            )}
          </FieldValue>
        )}

        {info?.vehicleStateAck && (
          <FieldValue
            variant='filled'
            direction='column'
            label={
              <>
                Vehicle state acknowledgement{" "}
                <AppIconButton
                  size='small'
                  tooltipProps={{ title: "Quick view acknowledgement details" }}
                  onClick={() => setIsVehicleStateAckViewModalOpen(true)}
                >
                  <AppIcon of='quickView' />
                </AppIconButton>
              </>
            }
            isEmpty={!info?.vehicleStateAck}
          >
            {vehicleStateAckPdfFileItem && (
              <FileListView
                files={[vehicleStateAckPdfFileItem]}
                maxVisibleFiles={1}
                itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                fileItemViewProps={{
                  actions: {
                    enabled: true,
                    click: true,
                    quickView: true,
                    download: true,
                    openInNewTab: true,
                    onClick: (fileItem) => {
                      setFileItemPdfToViewInModalOpen(fileItem);
                    },
                    onQuickView: (fileItem) => {
                      setFileItemPdfToViewInModalOpen(fileItem);
                    },
                  },
                }}
              />
            )}
          </FieldValue>
        )}
      </Box>

      {/* PDF view modal */}
      {fileItemPdfToViewInModalOpen && (
        <PdfViewerModal
          viewerProps={{ fileItem: fileItemPdfToViewInModalOpen, scale: 1.4 }}
          modalTitle={<Box component='span'>Vehicle state PDF</Box>}
          open={!!fileItemPdfToViewInModalOpen}
          onClose={() => {
            setFileItemPdfToViewInModalOpen(null);
          }}
        />
      )}

      {info?.vehicleStateAck && (
        <CustomerAcknowledgementDisplayModal
          open={isVehicleStateAckViewModalOpen}
          onClose={() => {
            setIsVehicleStateAckViewModalOpen(false);
          }}
          subProps={{
            value: info.vehicleStateAck,
            onDeletePersonalData: async (dto) => {
              const response =
                await apiClient.contractsApi.apiV1ContractsContractIdVehicleCheckOutAcknowledgementsVehicleStateCustomerPersonalDataDelete(
                  {
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    contractId: contract.id || "",
                    personalDataDeleteDto: dto,
                  },
                );
              onUpdate(response.data);
            },
          }}
          modelTitleProps={{
            title: `Customer acknowledgement of vehicle state on ${checkLabel?.toLocaleLowerCase()}`,
          }}
        />
      )}
    </Stack>
  );
}
